import { useMemo } from 'react';
import { Skeleton } from '@/components/ui/skeleton';
import { EmptyState } from './EmptyState';

const DOW = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

interface Props {
  matrix: number[][]; // [7][24]
  max: number;
  loading?: boolean;
  /** Hours to render (inclusive). Default 8–23 (8am to 11pm). */
  hourRange?: [number, number];
}

export function HourlyHeatmap({
  matrix,
  max,
  loading,
  hourRange = [8, 23],
}: Props) {
  const hours = useMemo(() => {
    const [start, end] = hourRange;
    return Array.from({ length: end - start + 1 }, (_, i) => start + i);
  }, [hourRange]);

  if (loading) {
    return <Skeleton className="h-48 w-full" />;
  }
  if (!matrix.length || max === 0) {
    return (
      <EmptyState
        title="No hourly data"
        description="No orders with timestamps in this period."
      />
    );
  }

  return (
    <div className="overflow-x-auto">
      <table className="w-full border-separate border-spacing-0.5">
        <thead>
          <tr>
            <th className="w-10" />
            {hours.map((h) => (
              <th
                key={h}
                className="px-1 text-center text-[10px] font-normal text-muted-foreground"
              >
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {DOW.map((label, dow) => (
            <tr key={label}>
              <th className="pr-2 text-right text-[10px] font-normal text-muted-foreground">
                {label}
              </th>
              {hours.map((h) => {
                const v = matrix[dow]?.[h] ?? 0;
                const intensity = max > 0 ? v / max : 0;
                return (
                  <td
                    key={h}
                    className="h-6 rounded-sm border border-border/40"
                    style={{
                      backgroundColor:
                        intensity === 0
                          ? 'hsl(var(--muted) / 0.3)'
                          : `hsl(var(--primary) / ${0.15 + intensity * 0.85})`,
                    }}
                    title={`${label} ${h}:00 — ${v.toFixed(1)} avg orders`}
                  />
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
      <div className="mt-2 flex items-center justify-end gap-2 text-2xs text-muted-foreground">
        <span>0</span>
        <div className="flex gap-0.5">
          {[0.15, 0.3, 0.5, 0.7, 0.9].map((o) => (
            <div
              key={o}
              className="h-3 w-3 rounded-sm border border-border/40"
              style={{ backgroundColor: `hsl(var(--primary) / ${o})` }}
            />
          ))}
        </div>
        <span>{max.toFixed(1)} avg orders / hr</span>
      </div>
    </div>
  );
}
